import { useCallback, useState } from 'react';

import { Alert, Autocomplete, Box, Button, TextField, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

import {
  CronScheduleBuilder,
  type CronScheduleBuilderHandle,
  type CronScheduleBuilderValue,
} from '@/components/cron';
import type { CronTimeFrame } from '@/types/schedule';

import type { AdvancedCronEditableScope } from '../types/advancedCronMigration';
import type { CronScheduleSaveState } from '../utils/cronScheduleSave';
import {
  ADVANCED_CRON_MIGRATION_TZ_CONFIRM_REQUIRED,
  ADVANCED_CRON_TZ_HELP,
} from '../utils/scheduleDefaults';
import {
  type TimezoneOption,
  filterTimezoneOptions,
  getBrowserTimezone,
  getTimezoneOptions,
  resolveTimezoneOption,
} from '../utils/timezoneData';

interface CronScheduleConfigProps {
  value: CronScheduleSaveState;
  onChange: (value: CronScheduleSaveState) => void;
  builderRef?: React.RefObject<CronScheduleBuilderHandle | null>;
  error?: string;
  disabled?: boolean;
  storedCronTimeZone?: string;
  storedCronTimeFrame?: CronTimeFrame | null;
  showLegacyMigrationNotice?: boolean;
  editableScope?: AdvancedCronEditableScope;
  timeframeUnconfirmed?: boolean;
  showStoredUtcExpression?: boolean;
  onConfirmTimezone?: () => void;
  onConfirmTimeframe?: () => void;
  onTimezoneMigrationChange?: () => void;
}

export const CronScheduleConfig = ({
  value,
  onChange,
  builderRef,
  disabled = false,
  storedCronTimeZone,
  storedCronTimeFrame,
  showLegacyMigrationNotice = false,
  editableScope,
  timeframeUnconfirmed = false,
  showStoredUtcExpression = false,
  onConfirmTimezone,
  onConfirmTimeframe,
  onTimezoneMigrationChange,
}: CronScheduleConfigProps) => {
  const [timezoneOptions] = useState<TimezoneOption[]>(() => getTimezoneOptions());
  const scope = editableScope ?? 'timezone-and-timeframe';
  const timezoneConfirmPending = scope === 'timezone-pending-confirmation';
  const selectedTimezoneOption = resolveTimezoneOption(value.timeZone, timezoneOptions);
  const selectedTimezoneLabel =
    selectedTimezoneOption?.label ?? value.timeZone ?? getBrowserTimezone();

  const handleChange = useCallback(
    (newValue: CronScheduleBuilderValue) => {
      onChange({
        ...value,
        expression: newValue.expression,
        explain: newValue.explain,
        previewExpression: newValue.previewExpression ?? value.previewExpression,
        previewConfigKey: newValue.previewConfigKey ?? value.previewConfigKey,
        isDirty: newValue.isDirty,
        isValid: newValue.isValid,
        canonicalExpression: newValue.canonicalExpression,
      });
    },
    [onChange, value]
  );

  const handleTimezoneChange = (_event: React.SyntheticEvent, newValue: TimezoneOption | null) => {
    onChange({
      ...value,
      timeZone: newValue?.value ?? getBrowserTimezone(),
      isDirty: timezoneConfirmPending ? value.isDirty : true,
    });
    onTimezoneMigrationChange?.();
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Configure an advanced schedule using cron expressions. Times are entered in the selected
        timezone and stored as UTC for EventBridge.
      </Typography>

      <Box
        sx={{
          mb: 2,
          ...(timezoneConfirmPending
            ? (theme) => ({
                border: 1,
                borderColor: 'error.main',
                borderRadius: 1,
                p: 2,
                bgcolor: alpha(theme.palette.error.main, 0.08),
              })
            : undefined),
        }}
      >
        {timezoneConfirmPending && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {ADVANCED_CRON_MIGRATION_TZ_CONFIRM_REQUIRED} Current selection:{' '}
            <strong>{selectedTimezoneLabel}</strong>.
          </Alert>
        )}

        <Autocomplete
          value={selectedTimezoneOption}
          onChange={handleTimezoneChange}
          options={timezoneOptions}
          filterOptions={filterTimezoneOptions}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, selected) => option.value === selected.value}
          disabled={disabled}
          size="small"
          renderInput={(params) => (
            <TextField
              {...params}
              label="Timezone"
              placeholder="Select timezone"
              helperText={ADVANCED_CRON_TZ_HELP}
              error={timezoneConfirmPending}
            />
          )}
        />

        {timezoneConfirmPending && onConfirmTimezone && (
          <Button variant="contained" color="error" onClick={onConfirmTimezone} sx={{ mt: 2 }}>
            Confirm timezone
          </Button>
        )}
      </Box>

      {showStoredUtcExpression && (value.canonicalExpression ?? value.expression) && (
        <Box sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
            Stored expression (UTC)
          </Typography>
          <Typography variant="body2" fontFamily="monospace">
            {value.canonicalExpression ?? value.expression}
          </Typography>
          {value.explain && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {value.explain}
            </Typography>
          )}
        </Box>
      )}

      <CronScheduleBuilder
        ref={builderRef}
        value={{
          expression: value.expression,
          explain: value.explain,
          previewExpression: value.previewExpression,
          previewConfigKey: value.previewConfigKey,
          isDirty: value.isDirty,
          isValid: value.isValid,
          canonicalExpression: value.canonicalExpression ?? value.expression,
        }}
        onChange={handleChange}
        timeZone={value.timeZone}
        storedCronTimeZone={storedCronTimeZone}
        storedCronTimeFrame={storedCronTimeFrame}
        disabled={disabled}
        editableScope={scope}
        timeframeUnconfirmed={timeframeUnconfirmed}
        onConfirmTimeframe={onConfirmTimeframe}
        showLegacyMigrationNotice={showLegacyMigrationNotice && scope === 'timezone-and-timeframe'}
      />
    </Box>
  );
};
